import { useMemo, useState } from "react";
import { Plus, Check, Clock, Calendar } from "lucide-react";
import Button from "../ui/Button";
import Card from "../ui/Card";
import Pill from "../ui/Pill";
import { Table, THead, TR, TH, TD, EmptyState } from "../ui/Table";
import DealForm from "./DealForm";
import { formatUSD } from "../../lib/formatters";
import { formatDate, toISO, today } from "../../lib/dateUtils";

// A "deal" = one or more payment rows sharing the same splitGroupId.
// Single-invoice deals (no splitGroupId) are shown as standalone deals.
function groupDeals(payments) {
  const groups = new Map();
  const singles = [];
  for (const p of payments) {
    if (p.splitGroupId) {
      if (!groups.has(p.splitGroupId)) groups.set(p.splitGroupId, []);
      groups.get(p.splitGroupId).push(p);
    } else {
      singles.push(p);
    }
  }
  // Sort invoices inside each group by dueDate.
  for (const arr of groups.values()) {
    arr.sort((a, b) => (a.dueDate || a.bookedDate || "").localeCompare(b.dueDate || b.bookedDate || ""));
  }
  return { groups, singles };
}

function dealLabel(invoices) {
  // Take the first invoice's note minus the "— Invoice X of Y" suffix.
  const first = invoices[0]?.note || "";
  return first.replace(/\s*—?\s*Invoice\s+\d+\s+of\s+\d+\s*$/i, "").trim() || "Deal";
}

function statusPill(status) {
  switch (status) {
    case "paid": return <Pill status="active">Paid</Pill>;
    case "pending": return <Pill status="pending">Pending</Pill>;
    case "scheduled": return <Pill status="oneoff">Scheduled</Pill>;
    case "cancelled": return <Pill status="cancelled">Cancelled</Pill>;
    default: return <Pill status="neutral">{status || "—"}</Pill>;
  }
}

export default function DealsSection({ client, payments, offers, dashboard }) {
  const [showAdd, setShowAdd] = useState(false);

  // Only this client's payments.
  const myPayments = useMemo(
    () => (payments || []).filter((p) => p.clientId === client.id),
    [payments, client.id]
  );

  const { groups, singles } = useMemo(() => groupDeals(myPayments), [myPayments]);
  const offerName = (id) => offers.find((o) => o.id === id)?.name || "—";

  const handleNewDeal = (rows) => {
    dashboard.addSplitPayments(rows);
  };

  const handleMarkPaid = (id) => {
    dashboard.markPaymentPaid(id, toISO(today()));
  };

  // Build a flat array of deals (split groups + singles) with totals.
  const deals = useMemo(() => {
    const out = [];
    for (const [groupId, rows] of groups.entries()) {
      const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
      const paid = rows.filter((r) => r.status === "paid").reduce((s, r) => s + (Number(r.amount) || 0), 0);
      out.push({
        kind: "split",
        id: groupId,
        rows,
        total,
        paid,
        offerId: rows[0]?.offerId || null,
        name: dealLabel(rows),
        firstDate: rows[0]?.dueDate || rows[0]?.bookedDate || "",
      });
    }
    for (const p of singles) {
      out.push({
        kind: "single",
        id: p.id,
        rows: [p],
        total: Number(p.amount) || 0,
        paid: p.status === "paid" ? Number(p.amount) || 0 : 0,
        offerId: p.offerId,
        name: p.note || offerName(p.offerId),
        firstDate: p.dueDate || p.bookedDate || p.paidDate || "",
      });
    }
    // Most-recent deal first.
    out.sort((a, b) => (b.firstDate || "").localeCompare(a.firstDate || ""));
    return out;
  }, [groups, singles, offers]);

  return (
    <Card
      title={`Deals (${deals.length})`}
      action={
        <Button onClick={() => setShowAdd(true)}>
          <Plus size={14} /> Add deal
        </Button>
      }
    >
      {deals.length === 0 ? (
        <EmptyState
          icon={Calendar}
          title="No deals yet"
          message="Add the first deal to schedule what this client will pay (single invoice or split into multiple)."
        />
      ) : (
        <div className="space-y-3">
          {deals.map((d) => {
            const remaining = d.total - d.paid;
            return (
              <div
                key={d.id}
                className="rounded-lg border border-gray-200 dark:border-[var(--border-soft)] overflow-hidden"
              >
                <div className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 dark:bg-[var(--bg-soft)] border-b border-gray-100 dark:border-[var(--border-soft)]">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-brand-ink dark:text-[var(--text-primary)] truncate">
                      {d.name}
                      <span className="ml-2 text-[11px] text-brand-slate font-normal">
                        {offerName(d.offerId)}
                      </span>
                    </div>
                    <div className="text-[11px] text-brand-slate">
                      {d.kind === "split" ? `${d.rows.length} invoices` : "1 invoice"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold">{formatUSD(d.total)}</div>
                    <div className="text-[11px]">
                      <span className="text-emerald-700 dark:text-emerald-300">{formatUSD(d.paid)} paid</span>
                      {remaining > 0 && (
                        <>
                          <span className="text-brand-slate mx-1">/</span>
                          <span className="text-amber-700 dark:text-amber-300">{formatUSD(remaining)} left</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <Table>
                  <THead>
                    <TR>
                      <TH>#</TH>
                      <TH>Due / Paid</TH>
                      <TH>Status</TH>
                      <TH className="text-right">Amount</TH>
                      <TH>Note</TH>
                      <TH></TH>
                    </TR>
                  </THead>
                  <tbody>
                    {d.rows.map((r, i) => (
                      <TR key={r.id}>
                        <TD className="text-xs text-brand-slate">{i + 1}</TD>
                        <TD className="text-xs">
                          {r.status === "paid" && r.paidDate
                            ? <span title="Paid date">{formatDate(r.paidDate)}</span>
                            : <span className="text-brand-slate" title="Due date">{formatDate(r.dueDate || r.bookedDate)}</span>}
                        </TD>
                        <TD>{statusPill(r.status)}</TD>
                        <TD className="text-right font-medium">{formatUSD(r.amount)}</TD>
                        <TD className="text-xs text-brand-slate truncate max-w-[200px]">{r.note || "—"}</TD>
                        <TD>
                          {r.status !== "paid" && (
                            <button
                              onClick={() => handleMarkPaid(r.id)}
                              className="text-xs text-brand-blue hover:underline inline-flex items-center gap-1"
                              title="Mark this invoice as paid today"
                            >
                              <Check size={11} /> Mark paid
                            </button>
                          )}
                        </TD>
                      </TR>
                    ))}
                  </tbody>
                </Table>
              </div>
            );
          })}
        </div>
      )}

      <DealForm
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onSubmit={handleNewDeal}
        client={client}
        offers={offers}
      />
    </Card>
  );
}
